import { motion } from 'framer-motion';
import { MessageCircle, Calendar, ExternalLink, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import PageMeta from '../components/PageMeta';

const Book = () => {
  return (
    <div data-testid="book-page">
      <PageMeta
        title="Book an Appointment | Ali Dorgan"
        description="Book an appointment with Ali Dorgan or one of our physiotherapists in Cobh, Cork. Message Ali on WhatsApp or book online."
      />
      {/* Hero */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
              Book
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-charcoal leading-tight mb-8">
              Book an Appointment
            </h1>
            <p className="text-lg md:text-xl text-charcoal/70 leading-relaxed max-w-2xl mx-auto">
              Choose your practitioner and preferred booking method below. Ali's appointments 
              are managed directly via WhatsApp due to demand, while other available 
              practitioners can be booked online.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lead Practitioner — Ali */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <Card className="bg-sage-50 border-0 shadow-medium rounded-3xl overflow-hidden">
              <CardContent className="p-10 md:p-14 lg:p-16">
                <div className="flex flex-col items-center text-center lg:items-start lg:text-left lg:flex-row lg:gap-16">
                  {/* Photo placeholder */}
                  <div className="shrink-0 mb-8 lg:mb-0">
                    <div className="w-40 h-40 lg:w-48 lg:h-48 bg-sage-200/60 rounded-full flex items-center justify-center">
                      <span className="font-serif text-5xl lg:text-6xl text-sage-400 font-light">AD</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-sage-600 font-semibold mb-4">
                      <Star className="w-4 h-4" />
                      Lead Practitioner
                    </span>

                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-charcoal mb-2">
                      Ali Dorgan
                    </h2>
                    <p className="text-sage-600 font-semibold text-lg mb-6">
                      Neuromuscular & Nutritional Therapist
                    </p>

                    <p className="text-charcoal/70 text-base md:text-lg leading-relaxed mb-4">
                      Experienced in injury treatment, hands-on therapy, and personalised nutrition support.
                    </p>

                    <p className="text-charcoal/50 text-sm mb-10">
                      Ali's appointments are managed directly to ensure the best fit and availability.
                    </p>

                    <Button
                      asChild
                      size="lg"
                      className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full gap-3 w-full sm:w-auto px-10 py-6 text-base font-semibold shadow-soft hover:shadow-medium hover:-translate-y-0.5 transition-all duration-300"
                      data-testid="book-ali-whatsapp"
                    >
                      <a href="https://wa.me/353877727580?text=Hi%20Ali%2C%20I%27d%20like%20to%20check%20your%20availability%20for%20an%20appointment." target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-5 h-5" />
                        Message Ali on WhatsApp
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Other Practitioners — Brian & Joy */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-4 block">
              Team
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-charcoal">
              Other Practitioners
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-10 max-w-4xl mx-auto">
            {/* Brian */}
            <PractitionerCard
              initials="BL"
              name="Brian Lawton"
              title="Physical Therapist"
              specialty="Sports injury, rehabilitation, performance support"
              bio="Brian Lawton is a highly experienced Physical Therapist with over 25 years of professional practice, having qualified in 1999. He has worked extensively with athletes across a wide range of sports and competitive levels."
              bioExtended="He has provided physical therapy support to organisations including Cobh GAA, College Corinthians, Cobh Ramblers U19, and Cork Camogie Minors. In 2013, Brian was part of the Cork team at the Race Across America (RAAM), one of the most demanding endurance events in the world."
              bookingUrl="https://www.fresha.com/book-now/alidorgan-nt-and-nmt-g0b0bzyf/services?lid=206215&eid=611079&oiid=sv%3A11859734&share=true&pId=192546"
              ctaLabel="Book with Brian"
              testId="book-brian"
              delay={0}
            />

            {/* Joy */}
            <PractitionerCard
              initials="J"
              name="Joy"
              title="Neuromuscular Therapist"
              specialty="Soft tissue therapy, mobility, yoga-informed treatment"
              bio="Joy is a qualified Neuromuscular Therapist trained with the NTC and HCD. She joined Ali's clinic in 2025 and has enjoyed working with the local community in Cobh. Her focus is on helping clients alleviate soft tissue pain and discomfort while improving overall function."
              bioExtended="In addition to her manual therapy work, Joy is also a qualified yoga teacher. She has been practicing yoga for over 10 years and spent 10 months in India completing her teacher training."
              bookingUrl="https://www.fresha.com/book-now/alidorgan-nt-and-nmt-g0b0bzyf/services?lid=206215&eid=611079&oiid=sv%3A23043269&share=true&pId=192546"
              ctaLabel="Book with Joy"
              testId="book-joy"
              delay={0.1}
            />
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-sage-900">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-cream-200/80 text-lg mb-6">
              Questions about booking? Get in touch directly.
            </p>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="rounded-full border-2 border-cream-100 text-cream-100 hover:bg-cream-100 hover:text-sage-900 px-10 py-6 font-semibold transition-all duration-300"
              data-testid="book-contact-btn"
            >
              <a href="/contact">
                Contact Us
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const PractitionerCard = ({ initials, name, title, specialty, bio, bioExtended, bookingUrl, ctaLabel, testId, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
  >
    <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl overflow-hidden">
      <CardContent className="p-10 flex flex-col items-center text-center">
        {/* Avatar */}
        <div className="w-24 h-24 bg-cream-100 rounded-full flex items-center justify-center mb-6">
          <span className="font-serif text-3xl text-charcoal/30 font-light">{initials}</span>
        </div>

        {/* Info */}
        <h3 className="font-serif text-2xl font-medium text-charcoal mb-1">{name}</h3>
        <p className="text-sage-600 text-sm font-semibold mb-3">{title}</p>

        <p className="text-xs uppercase tracking-wider text-charcoal/40 font-medium mb-5">
          {specialty}
        </p>

        <p className="text-charcoal/60 text-base leading-relaxed mb-3">
          {bio}
        </p>

        {bioExtended && (
          <p className="text-charcoal/60 text-base leading-relaxed mb-4">
            {bioExtended}
          </p>
        )}

        {!bioExtended && <div className="mb-4" />}

        <p className="text-sage-600 text-xs font-semibold mb-5 flex items-center justify-center gap-1.5">
          <Calendar className="w-3.5 h-3.5" />
          Online booking available
        </p>

        {/* CTA */}
        <Button
          asChild
          size="lg"
          className="w-full rounded-full bg-sage-600 hover:bg-sage-500 text-white py-6 font-semibold gap-2 transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md mt-auto"
          data-testid={testId}
        >
          <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
            <Calendar className="w-5 h-5" />
            {ctaLabel || 'Book Appointment'}
          </a>
        </Button>
      </CardContent>
    </Card>
  </motion.div>
);

export default Book;
